import React from 'react';
import { motion } from 'framer-motion';
import { Book, PenTool as Tool, Briefcase } from 'lucide-react';
import { generateIndustryTips } from '../data/industryRecommendations';
import type { UserProfile } from '../types';

interface IndustryRecommendationsProps {
  userProfile: UserProfile;
}

export function IndustryRecommendations({ userProfile }: IndustryRecommendationsProps) {
  const recommendations = generateIndustryTips(userProfile);

  return (
    <div className="space-y-6">
      {/* 经典案例 */}
      <div className="p-6 bg-gradient-to-r from-[#1A1F36] to-[#2A2E4D] rounded-xl border border-[#2D3655]">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Book className="w-6 h-6 text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold">经典案例</h3>
        </div>
        <div className="space-y-4">
          {recommendations.caseStudies.map((study, index) => (
            <motion.a
              key={index}
              href={study.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 bg-[#1A1F36] rounded-lg border border-[#2D3655] hover:border-blue-500/50 transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <h4 className="font-medium text-blue-400 mb-2">{study.title}</h4>
              <p className="text-sm text-gray-400">{study.description}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {study.tags.map((tag, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-blue-500/10 text-blue-300 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.a>
          ))}
        </div>
      </div>

      {/* 推荐工具 */}
      <div className="p-6 bg-gradient-to-r from-[#1A1F36] to-[#2A2E4D] rounded-xl border border-[#2D3655]">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Tool className="w-6 h-6 text-purple-400" />
          </div>
          <h3 className="text-lg font-semibold">推荐工具</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recommendations.tools.map((tool, index) => (
            <motion.a
              key={index}
              href={tool.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-4 bg-[#1A1F36] rounded-lg border border-[#2D3655] hover:border-purple-500/50 transition-all"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <img src={tool.icon} alt={tool.name} className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-medium text-purple-400">{tool.name}</h4>
                  <p className="text-xs text-gray-400">{tool.category}</p>
                </div>
              </div>
              <p className="text-sm text-gray-400">{tool.description}</p>
            </motion.a>
          ))}
        </div>
      </div>

      {/* RWA投资机会 */}
      <div className="p-6 bg-gradient-to-r from-[#1A1F36] to-[#2A2E4D] rounded-xl border border-[#2D3655]">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-green-500/20 rounded-lg">
            <Briefcase className="w-6 h-6 text-green-400" />
          </div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold">
              <a
                href="https://www.da-age.io"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                DAAGE RWA投资机会
              </a>
            </h3>
            <a
              href="https://www.da-age.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full hover:bg-green-500/30 transition-colors"
            >
              了解更多
            </a>
          </div>
        </div>
        <div className="space-y-4">
          {recommendations.opportunities.rwa.map((project, index) => (
            <motion.div
              key={index}
              className="p-4 bg-[#1A1F36] rounded-lg border border-[#2D3655]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <h4 className="font-medium text-green-400 mb-2">{project.name}</h4>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-400">最小投资额</p>
                  <p className="text-lg font-semibold text-white">
                    ${project.minInvestment.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">预期年化</p>
                  <p className="text-lg font-semibold text-white">
                    {(project.expectedReturn * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
              <div className="text-sm text-gray-400">
                <p>每月稳定收益：${((project.minInvestment * project.expectedReturn) / 12).toLocaleString()}</p>
                <p>八年累计收益：${(project.minInvestment * project.expectedReturn * 8).toLocaleString()}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}